import { motion, AnimatePresence, LayoutGroup } from 'motion/react';
import { useLocation } from 'react-router-dom';
import React, { useRef, useEffect, useCallback, type ReactNode } from 'react';
import AnimatedBackgroundLayer from './AnimatedBackgroundLayer';

interface ScreenTransitionProps {
  children: ReactNode;
  layoutId?: string;
}


const ScreenTransition: React.FC<ScreenTransitionProps> = ({ children, layoutId }) => {
  const location = useLocation();

  return (
    <LayoutGroup id="screen-transitions">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          layoutId={layoutId || `screen-${location.pathname}`}
          initial={{ opacity: 0, scale: 0.95, y: 20, filter: 'blur(5px)' }}
          animate={{ opacity: 1, scale: 1, y: 0, filter: 'blur(0px)' }}
          exit={{ opacity: 0, scale: 1.05, y: -20, filter: 'blur(3px)' }}
          transition={{
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
          }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </LayoutGroup>
  );
};

// Screen container for consistent layouts
// NOTE: Only animates opacity here. The parent ScreenTransition handles y/scale/blur
// to avoid conflicting y animations that can cause stuck transitions.
export const ScreenContainer: React.FC<{
  children: ReactNode;
  className?: string;
  layoutId?: string;
}> = ({ children, className = '', layoutId }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Fallback: ensure element reaches final visible state even if animation fails
  const ensureVisible = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const computed = window.getComputedStyle(el);
    const opacity = parseFloat(computed.opacity);
    if (opacity < 0.95) {
      el.style.opacity = '1';
    }
  }, []);

  useEffect(() => {
    const t1 = setTimeout(ensureVisible, 500);
    const t2 = setTimeout(ensureVisible, 1200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [ensureVisible]);

  return (
    <motion.div
      ref={containerRef}
      layoutId={layoutId}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeOut',
      }}
      onAnimationComplete={() => ensureVisible()}
      className={`screen-container ${className}`}
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 1,
      }}
    >
      {children}
    </motion.div>
  );
};

// Shared element wrapper for elements that should transition between screens
export const SharedElement: React.FC<{
  layoutId: string;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = ({ layoutId, children, className, style }) => {
  return (
    <motion.div
      layoutId={layoutId}
      className={className}
      style={style}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
    >
      {children}
    </motion.div>
  );
};

// Character transition wrapper for maintaining character between screens
export const CharacterTransition: React.FC<{
  children: ReactNode;
  characterId?: string;
  position?: { x: number; y: number };
}> = ({ children, characterId = 'main-character', position }) => {
  return (
    <motion.div
      layoutId={`character-${characterId}`}
      style={{
        position: 'absolute',
        ...(position && {
          left: position.x,
          top: position.y,
        }),
      }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25,
      }}
    >
      {children}
    </motion.div>
  );
};

// Background transition for maintaining environment consistency
export const BackgroundTransition: React.FC<{
  theme?: 'home' | 'game' | 'shop' | 'levels' | 'default';
  animated?: boolean;
  performance?: 'high' | 'medium' | 'low';
}> = ({ theme = 'default', animated = true, performance = 'medium' }) => {
  return (
    <motion.div
      layoutId={`background-${theme}`}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
      }}
      transition={{
        duration: 0.6,
        ease: 'easeInOut',
      }}
    >
      {animated && theme !== 'default' && (
        <AnimatedBackgroundLayer 
          theme={theme}
          performance={performance}
        />
      )}
    </motion.div>
  );
};

export default ScreenTransition;